import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { AppModal } from '../../../components/ui/app-modal';
import { Button } from '../../../components/ui/button';
import { DatetimePicker } from '../../../components/ui/datetimePicker';
import { cn } from '../../../lib/utils';

// ── Types ──────────────────────────────────────────────────────────────────────

export type SiteVisitStatus =
  | 'NOT_STARTED'
  | 'CUSTOMER_APPROVAL'
  | 'APPROVED'
  | 'IN_PROGRESS'
  | 'COMPLETED';

// ── Status config ──────────────────────────────────────────────────────────────

const STATUS_CFG: Record<SiteVisitStatus, { label: string; className: string }> = {
  NOT_STARTED: { label: 'Not Started', className: 'bg-gray-100 text-gray-600 border-gray-200' },
  CUSTOMER_APPROVAL: {
    label: 'Awaiting Approval',
    className: 'bg-warning-50 text-warning-600 border-warning-200',
  },
  APPROVED: { label: 'Approved', className: 'bg-success-50 text-success-600 border-success-200' },
  IN_PROGRESS: { label: 'In Progress', className: 'bg-brand-50 text-brand-600 border-brand-200' },
  COMPLETED: {
    label: 'Completed',
    className: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
};

export function StatusBadge({ status }: { status: SiteVisitStatus }) {
  const cfg = STATUS_CFG[status];
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold',
        cfg.className,
      )}
    >
      {cfg.label}
    </span>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

export function fmt(iso: string | null | undefined) {
  if (!iso) return null;
  return new Intl.DateTimeFormat('en-AE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

// ── Complete Modal ─────────────────────────────────────────────────────────────

export function CompleteModal({
  open,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: (comments: string) => void;
  isPending: boolean;
}) {
  const [comments, setComments] = useState('');

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Complete Site Visit"
      description="Optionally add completion notes."
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="complete-comments">
            Completion Notes (optional)
          </label>
          <textarea
            id="complete-comments"
            rows={3}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder="Findings, observations…"
            className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button className="flex-1" onClick={() => onSubmit(comments)} disabled={isPending}>
            {isPending ? 'Completing…' : 'Mark Completed'}
          </Button>
        </div>
      </div>
    </AppModal>
  );
}

// ── Reschedule Modal ───────────────────────────────────────────────────────────

export function RescheduleModal({
  open,
  currentScheduledAt,
  onClose,
  onSubmit,
  isPending,
}: {
  open: boolean;
  currentScheduledAt: string | null;
  onClose: () => void;
  onSubmit: (form: { scheduledAt: string; reason: string }) => void;
  isPending: boolean;
}) {
  const [scheduledAt, setScheduledAt] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setScheduledAt(currentScheduledAt ?? '');
      setReason('');
    }
  }, [open, currentScheduledAt]);

  const isValid = !!scheduledAt && reason.trim().length > 0;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Reschedule Site Visit"
      description="Pick a new date & time and provide a reason for the reschedule."
    >
      <div className="space-y-4">
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600">New Date &amp; Time</label>
          <DatetimePicker
            value={scheduledAt ? new Date(scheduledAt) : undefined}
            onChange={(date) => setScheduledAt(date ? date.toISOString() : '')}
            granularity="minute"
            placeholder="Pick date & time"
            className="w-full"
          />
        </div>
        <div className="space-y-1">
          <label className="block text-xs font-medium text-gray-600" htmlFor="reschedule-reason">
            Reason <span className="text-error-500">*</span>
          </label>
          <textarea
            id="reschedule-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why is this visit being rescheduled?"
            className="w-full resize-none rounded-xl border border-gray-200 bg-gray-50 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-100"
          />
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="flex-1" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="flex-1"
            onClick={() => onSubmit({ scheduledAt, reason: reason.trim() })}
            disabled={!isValid || isPending}
          >
            {isPending ? 'Rescheduling…' : 'Reschedule'}
          </Button>
        </div>
      </div>
    </AppModal>
  );
}

// ── Attachment Preview Modal ────────────────────────────────────────────────────

export interface PreviewAttachment {
  fileName: string;
  mimeType: string;
  url: string;
  title?: string;
}

function isImageMime(mimeType: string) {
  return mimeType.startsWith('image/');
}

export function AttachmentPreviewModal({
  attachment,
  onClose,
}: {
  attachment: PreviewAttachment | null;
  onClose: () => void;
}) {
  if (!attachment) return null;

  return (
    <div className="fixed inset-0 z-[100000] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-md" onClick={onClose} />
      <div className="relative z-10 flex w-full max-w-3xl max-h-[90vh] flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="min-w-0">
            {attachment.title && (
              <p className="text-sm font-semibold text-gray-900">{attachment.title}</p>
            )}
            <p className="max-w-sm truncate text-xs text-gray-400">{attachment.fileName}</p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={attachment.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs font-medium text-brand-600 underline hover:text-brand-700"
            >
              Open in new tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center overflow-auto bg-gray-50 p-4">
          {isImageMime(attachment.mimeType) ? (
            <img
              src={attachment.url}
              alt={attachment.fileName}
              className="max-h-full max-w-full rounded object-contain"
            />
          ) : attachment.mimeType === 'application/pdf' ? (
            <iframe
              src={attachment.url}
              title={attachment.fileName}
              className="h-[70vh] w-full rounded border-0"
            />
          ) : (
            <div className="py-12 text-center">
              <p className="mb-3 text-sm text-gray-500">
                Preview not available for this file type.
              </p>
              <a
                href={attachment.url}
                target="_blank"
                rel="noreferrer"
                className="text-sm font-medium text-brand-600 underline hover:text-brand-700"
              >
                Download / Open file
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
